import React, { createContext, useContext, useReducer, useCallback, useState, ReactNode } from 'react';
import {
  Work,
  CreateWorkDto,
  UpdateWorkDto,
  ProgressUpdateData,
  WorkFilters,
  WorkSummary,
  DashboardData,
  PerformanceAnalytics,
  ProgressTracking,
  WorkStage,
  DEFAULT_FILTERS
} from '../types/work.types';
import { worksApiService } from '../services/workApi.service';

// ==========================================
// STATE INTERFACES
// ==========================================

interface WorksState {
  // Works data
  works: Work[];
  currentWork: Work | null;
  summary: WorkSummary | null;
  
  // Dashboard data
  dashboardData: DashboardData | null;
  performanceData: PerformanceAnalytics | null;
  progressData: ProgressTracking | null;
  
  // UI state
  filters: WorkFilters;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  
  // Loading states
  loading: {
    works: boolean;
    dashboard: boolean;
    analytics: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  
  // Error state
  error: string | null;
  
  // Cache
  lastFetched: {
    works: Date | null;
    dashboard: Date | null;
    analytics: Date | null;
  };
}

// ==========================================
// ACTION TYPES
// ==========================================

type WorksAction =
  | { type: 'SET_LOADING'; payload: { key: keyof WorksState['loading']; loading: boolean } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_WORKS'; payload: { works: Work[]; summary: WorkSummary; pagination: any } }
  | { type: 'SET_CURRENT_WORK'; payload: Work | null }
  | { type: 'SET_DASHBOARD_DATA'; payload: DashboardData }
  | { type: 'SET_PERFORMANCE_DATA'; payload: PerformanceAnalytics }
  | { type: 'SET_PROGRESS_DATA'; payload: ProgressTracking }
  | { type: 'SET_FILTERS'; payload: WorkFilters }
  | { type: 'ADD_WORK'; payload: Work }
  | { type: 'UPDATE_WORK'; payload: Work }
  | { type: 'DELETE_WORK'; payload: number }
  | { type: 'CLEAR_CACHE'; payload?: string }
  | { type: 'RESET_STATE' };

// ==========================================
// INITIAL STATE
// ==========================================

const initialState: WorksState = {
  works: [],
  currentWork: null,
  summary: null,
  dashboardData: null,
  performanceData: null,
  progressData: null,
  filters: DEFAULT_FILTERS,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 50
  },
  loading: {
    works: false,
    dashboard: false,
    analytics: false,
    create: false,
    update: false,
    delete: false
  },
  error: null,
  lastFetched: {
    works: null,
    dashboard: null,
    analytics: null
  }
};

// ==========================================
// REDUCER
// ==========================================

const worksReducer = (state: WorksState, action: WorksAction): WorksState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.loading
        }
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };

    case 'SET_WORKS':
      return {
        ...state,
        works: action.payload.works,
        summary: action.payload.summary,
        pagination: action.payload.pagination,
        lastFetched: {
          ...state.lastFetched,
          works: new Date()
        },
        error: null
      };

    case 'SET_CURRENT_WORK':
      return {
        ...state,
        currentWork: action.payload
      };

    case 'SET_DASHBOARD_DATA':
      return {
        ...state,
        dashboardData: action.payload,
        lastFetched: {
          ...state.lastFetched,
          dashboard: new Date()
        },
        error: null
      };

    case 'SET_PERFORMANCE_DATA':
      return {
        ...state,
        performanceData: action.payload,
        lastFetched: {
          ...state.lastFetched,
          analytics: new Date()
        },
        error: null
      };

    case 'SET_PROGRESS_DATA':
      return {
        ...state,
        progressData: action.payload,
        error: null
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: action.payload
      };

    case 'ADD_WORK':
      return {
        ...state,
        works: [action.payload, ...state.works],
        summary: state.summary ? {
          ...state.summary,
          total_works: state.summary.total_works + 1,
          total_estimate_cost: state.summary.total_estimate_cost + action.payload.estimate_cost
        } : null
      };

    case 'UPDATE_WORK':
      return {
        ...state,
        works: state.works.map(work => 
          work.work_id === action.payload.work_id ? action.payload : work
        ),
        currentWork: state.currentWork?.work_id === action.payload.work_id ? action.payload : state.currentWork
      };

    case 'DELETE_WORK':
      return {
        ...state,
        works: state.works.filter(work => work.work_id !== action.payload),
        currentWork: state.currentWork?.work_id === action.payload ? null : state.currentWork,
        summary: state.summary ? {
          ...state.summary,
          total_works: state.summary.total_works - 1
        } : null
      };

    case 'CLEAR_CACHE':
      const clearedLastFetched = { ...state.lastFetched };
      if (action.payload) {
        clearedLastFetched[action.payload as keyof typeof clearedLastFetched] = null;
      } else {
        Object.keys(clearedLastFetched).forEach(key => {
          clearedLastFetched[key as keyof typeof clearedLastFetched] = null;
        });
      }
      return {
        ...state,
        lastFetched: clearedLastFetched
      };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
};

// ==========================================
// CONTEXT INTERFACE
// ==========================================

interface WorksContextValue {
  // State
  state: WorksState;
  
  // Works operations
  fetchWorks: (filters?: WorkFilters) => Promise<void>;
  fetchWorkById: (id: number) => Promise<Work>;
  createWork: (work: CreateWorkDto) => Promise<Work>;
  updateWork: (id: number, work: UpdateWorkDto) => Promise<Work>;
  updateWorkProgress: (id: number, progress: ProgressUpdateData) => Promise<Work>;
  deleteWork: (id: number) => Promise<void>;
  
  // Dashboard operations
  fetchDashboardData: () => Promise<void>;
  fetchPerformanceData: () => Promise<void>;
  fetchProgressData: () => Promise<void>;
  
  // Filter operations
  setFilters: (filters: WorkFilters) => void;
  resetFilters: () => void;
  
  // Utility operations
  clearError: () => void;
  clearCache: (section?: string) => void;
  refreshAll: () => Promise<void>;
}

// ==========================================
// CONTEXT CREATION
// ==========================================

const WorksContext = createContext<WorksContextValue | undefined>(undefined);

// ==========================================
// PROVIDER COMPONENT
// ==========================================

interface WorksProviderProps {
  children: ReactNode;
}

export const WorksProvider: React.FC<WorksProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(worksReducer, initialState);

  // ==========================================
  // LOADING HELPERS
  // ==========================================

  const setLoading = useCallback((key: keyof WorksState['loading'], loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: { key, loading } });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  // ==========================================
  // WORKS OPERATIONS
  // ==========================================

  const fetchWorks = useCallback(async (filters: WorkFilters = state.filters) => {
    try {
      setLoading('works', true);
      setError(null);
      
      const response = await worksApiService.getAllWorks(filters);
      
      dispatch({
        type: 'SET_WORKS',
        payload: {
          works: response.works,
          summary: response.summary,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            totalItems: response.total,
            pageSize: filters.limit || 50
          }
        }
      });
      
      // Update filters if they changed
      if (JSON.stringify(filters) !== JSON.stringify(state.filters)) {
        dispatch({ type: 'SET_FILTERS', payload: filters });
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch works');
    } finally {
      setLoading('works', false);
    }
  }, [state.filters, setLoading, setError]);

  const fetchWorkById = useCallback(async (id: number): Promise<Work> => {
    try {
      setError(null);
      const work = await worksApiService.getWorkById(id);
      dispatch({ type: 'SET_CURRENT_WORK', payload: work });
      return work;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch work';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [setError]);

  const createWork = useCallback(async (workData: CreateWorkDto): Promise<Work> => {
    try {
      setLoading('create', true);
      setError(null);
      
      const newWork = await worksApiService.createWork(workData);
      dispatch({ type: 'ADD_WORK', payload: newWork });
      
      return newWork;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create work';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading('create', false);
    }
  }, [setLoading, setError]);

  const updateWork = useCallback(async (id: number, workData: UpdateWorkDto): Promise<Work> => {
    try {
      setLoading('update', true);
      setError(null);
      
      const updatedWork = await worksApiService.updateWork(id, workData);
      dispatch({ type: 'UPDATE_WORK', payload: updatedWork });
      
      return updatedWork;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update work';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading('update', false);
    }
  }, [setLoading, setError]);

  const updateWorkProgress = useCallback(async (id: number, progress: ProgressUpdateData): Promise<Work> => {
    try {
      setLoading('update', true);
      setError(null);
      
      const updatedWork = await worksApiService.updateWorkProgress(id, progress);
      dispatch({ type: 'UPDATE_WORK', payload: updatedWork });
      
      return updatedWork;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update work progress';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading('update', false);
    }
  }, [setLoading, setError]);

  const deleteWork = useCallback(async (id: number): Promise<void> => {
    try {
      setLoading('delete', true);
      setError(null);
      
      await worksApiService.deleteWork(id);
      dispatch({ type: 'DELETE_WORK', payload: id });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete work';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading('delete', false);
    }
  }, [setLoading, setError]);

  // ==========================================
  // DASHBOARD OPERATIONS
  // ==========================================

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading('dashboard', true);
      setError(null);
      
      const dashboardData = await worksApiService.getFinancialDashboard();
      dispatch({ type: 'SET_DASHBOARD_DATA', payload: dashboardData });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading('dashboard', false);
    }
  }, [setLoading, setError]);

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading('analytics', true);
      setError(null);
      
      const performanceData = await worksApiService.getPerformanceAnalytics();
      dispatch({ type: 'SET_PERFORMANCE_DATA', payload: performanceData });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch performance data');
    } finally {
      setLoading('analytics', false);
    }
  }, [setLoading, setError]);

  const fetchProgressData = useCallback(async () => {
    try {
      const progressData = await worksApiService.getProgressTracking();
      dispatch({ type: 'SET_PROGRESS_DATA', payload: progressData });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch progress data');
    }
  }, [setError]);

  // ==========================================
  // FILTER OPERATIONS
  // ==========================================

  const setFilters = useCallback((filters: WorkFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: 'SET_FILTERS', payload: DEFAULT_FILTERS });
  }, []);

  // ==========================================
  // UTILITY OPERATIONS
  // ==========================================

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const clearCache = useCallback((section?: string) => {
    dispatch({ type: 'CLEAR_CACHE', payload: section });
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      await Promise.all([
        fetchWorks(state.filters),
        fetchDashboardData(),
        fetchPerformanceData(),
        fetchProgressData()
      ]);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to refresh data');
    }
  }, [state.filters, fetchWorks, fetchDashboardData, fetchPerformanceData, fetchProgressData, setError]);

  // ==========================================
  // CONTEXT VALUE
  // ==========================================

  const contextValue: WorksContextValue = {
    state,
    fetchWorks,
    fetchWorkById,
    createWork,
    updateWork,
    updateWorkProgress,
    deleteWork,
    fetchDashboardData,
    fetchPerformanceData,
    fetchProgressData,
    setFilters,
    resetFilters,
    clearError,
    clearCache,
    refreshAll
  };

  return (
    <WorksContext.Provider value={contextValue}>
      {children}
    </WorksContext.Provider>
  );
};

// ==========================================
// HOOK FOR USING CONTEXT
// ==========================================

export const useWorks = (): WorksContextValue => {
  const context = useContext(WorksContext);
  if (context === undefined) {
    throw new Error('useWorks must be used within a WorksProvider');
  }
  return context;
};

// ==========================================
// SELECTOR HOOKS FOR SPECIFIC DATA
// ==========================================

export const useWorksData = () => {
  const { state } = useWorks();
  return {
    works: state.works,
    summary: state.summary,
    loading: state.loading.works,
    error: state.error
  };
};

export const useDashboardData = () => {
  const { state } = useWorks();
  return {
    data: state.dashboardData,
    loading: state.loading.dashboard,
    error: state.error
  };
};

export const useAnalyticsData = () => {
  const { state } = useWorks();
  return {
    performance: state.performanceData,
    progress: state.progressData,
    loading: state.loading.analytics,
    error: state.error
  };
};

export const useCurrentWork = () => {
  const { state } = useWorks();
  return {
    work: state.currentWork,
    error: state.error
  };
};

export const useWorksFilters = () => {
  const { state, setFilters, resetFilters } = useWorks();
  return {
    filters: state.filters,
    setFilters,
    resetFilters,
    pagination: state.pagination
  };
};

// ==========================================
// CACHE MANAGEMENT HOOKS
// ==========================================

export const useWorksCache = () => {
  const { state, clearCache, refreshAll, fetchWorks, fetchDashboardData, fetchPerformanceData } = useWorks();
  
  const isCacheExpired = (section: keyof WorksState['lastFetched'], maxAge: number = 5 * 60 * 1000): boolean => {
    const lastFetched = state.lastFetched[section];
    if (!lastFetched) return true;
    return Date.now() - lastFetched.getTime() > maxAge;
  };

  const refreshIfExpired = useCallback(async (section: keyof WorksState['lastFetched']) => {
    if (isCacheExpired(section)) {
      switch (section) {
        case 'works':
          return fetchWorks();
        case 'dashboard':
          return fetchDashboardData();
        case 'analytics':
          return fetchPerformanceData();
      }
    }
  }, [isCacheExpired, fetchWorks, fetchDashboardData, fetchPerformanceData]);

  return {
    lastFetched: state.lastFetched,
    isCacheExpired,
    clearCache,
    refreshAll,
    refreshIfExpired
  };
};

// ==========================================
// BATCH OPERATIONS HOOK
// ==========================================

export const useWorksBatch = () => {
  const { state, fetchWorks } = useWorks();
  
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  const batchUpdateStages = useCallback(async (
    workIds: number[], 
    newStage: WorkStage, 
    subStage?: string
  ) => {
    try {
      setBatchLoading(true);
      setBatchError(null);
      
      const result = await worksApiService.batchUpdateStages(workIds, newStage, subStage);
      
      if (result.failed > 0) {
        setBatchError(`${result.failed} works failed to update. Check individual errors.`);
      }
      
      // Refresh works list
      await fetchWorks(state.filters);
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to batch update stages';
      setBatchError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setBatchLoading(false);
    }
  }, [fetchWorks, state.filters]);

  const batchAssignContractor = useCallback(async (
    workIds: number[], 
    contractorId: number
  ) => {
    try {
      setBatchLoading(true);
      setBatchError(null);
      
      const result = await worksApiService.batchAssignContractor(workIds, contractorId);
      
      if (result.failed > 0) {
        setBatchError(`${result.failed} works failed to update. Check individual errors.`);
      }
      
      // Refresh works list
      await fetchWorks(state.filters);
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to batch assign contractor';
      setBatchError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setBatchLoading(false);
    }
  }, [fetchWorks, state.filters]);

  return {
    batchLoading,
    batchError,
    batchUpdateStages,
    batchAssignContractor,
    clearBatchError: () => setBatchError(null)
  };
};

// ==========================================
// EXPORT/IMPORT OPERATIONS HOOK
// ==========================================

export const useWorksExport = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportToExcel = useCallback(async (filters: WorkFilters = {}) => {
    try {
      setExportLoading(true);
      setExportError(null);
      
      await worksApiService.downloadExcelExport(filters);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to export data';
      setExportError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  }, []);

  const importFromExcel = useCallback(async (file: File) => {
    try {
      setExportLoading(true);
      setExportError(null);
      
      const result = await worksApiService.importFromExcel(file);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import data';
      setExportError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  }, []);

  const validateImport = useCallback(async (file: File) => {
    try {
      setExportLoading(true);
      setExportError(null);
      
      const result = await worksApiService.validateExcelImport(file);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to validate import';
      setExportError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setExportLoading(false);
    }
  }, []);

  return {
    exportLoading,
    exportError,
    exportToExcel,
    importFromExcel,
    validateImport,
    clearExportError: () => setExportError(null)
  };
};

// ==========================================
// STATISTICS HOOK
// ==========================================

export const useWorksStats = () => {
  const { state } = useWorks();
  
  const getQuickStats = useCallback(() => {
    if (!state.summary) return null;
    
    return {
      totalWorks: state.summary.total_works,
      totalSanctioned: state.summary.total_estimate_cost,
      totalSpent: state.summary.total_expenditure,
      utilizationPercentage: state.summary.financial_progress_percentage,
      physicalProgress: state.summary.physical_progress_percentage,
      avgWorkCost: state.summary.avg_estimate_cost,
      pendingBillsAmount: state.summary.total_bills_pending + state.summary.total_work_not_recorded
    };
  }, [state.summary]);

  const getWorksByStage = useCallback(() => {
    if (!state.works) return {};
    
    return state.works.reduce((acc, work) => {
      const stage = work.abstract_stage_of_work;
      acc[stage] = (acc[stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [state.works]);

  const getDelayedWorks = useCallback(() => {
    if (!state.works) return [];
    
    return state.works.filter(work => 
      work.completion_date_set && 
      new Date(work.completion_date_set) < new Date() &&
      work.abstract_stage_of_work !== WorkStage.COMPLETED
    );
  }, [state.works]);

  const getHighValueWorks = useCallback((threshold: number = 1000000) => {
    if (!state.works) return [];
    
    return state.works.filter(work => 
      (work.ras_amount || work.estimate_cost) >= threshold
    );
  }, [state.works]);

  return {
    quickStats: getQuickStats(),
    worksByStage: getWorksByStage(),
    delayedWorks: getDelayedWorks(),
    getHighValueWorks,
    totalWorks: state.works.length
  };
};

// ==========================================
// PERFORMANCE MONITORING HOOK
// ==========================================

export const useWorksPerformance = () => {
  const { state } = useWorks();
  const [performanceMetrics, setPerformanceMetrics] = useState({
    apiCallsCount: 0,
    averageResponseTime: 0,
    errorRate: 0,
    cacheHitRate: 0
  });

  const trackApiCall = useCallback((responseTime: number, success: boolean) => {
    setPerformanceMetrics(prev => ({
      ...prev,
      apiCallsCount: prev.apiCallsCount + 1,
      averageResponseTime: (prev.averageResponseTime * (prev.apiCallsCount - 1) + responseTime) / prev.apiCallsCount,
      errorRate: success ? prev.errorRate : (prev.errorRate * (prev.apiCallsCount - 1) + 1) / prev.apiCallsCount
    }));
  }, []);

  const getMemoryUsage = useCallback(() => {
    const worksSize = JSON.stringify(state.works).length;
    const dashboardSize = JSON.stringify(state.dashboardData).length;
    const totalSize = worksSize + dashboardSize;
    
    return {
      worksSize: Math.round(worksSize / 1024), // KB
      dashboardSize: Math.round(dashboardSize / 1024), // KB
      totalSize: Math.round(totalSize / 1024) // KB
    };
  }, [state.works, state.dashboardData]);

  return {
    performanceMetrics,
    memoryUsage: getMemoryUsage(),
    trackApiCall
  };
};

// ==========================================
// REAL-TIME UPDATES HOOK
// ==========================================

export const useWorksRealTime = () => {
  const { refreshAll } = useWorks();
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // This would connect to WebSocket or Server-Sent Events
  const connectToRealTimeUpdates = useCallback(() => {
    // Placeholder for real-time connection
    // In a real implementation, you would:
    // 1. Connect to WebSocket
    // 2. Listen for work updates
    // 3. Refresh specific data when updates received
    
    const interval = setInterval(async () => {
      if (document.visibilityState === 'visible') {
        await refreshAll();
        setLastUpdate(new Date());
      }
    }, 60000); // Refresh every minute when tab is visible

    setIsConnected(true);

    return () => {
      clearInterval(interval);
      setIsConnected(false);
    };
  }, [refreshAll]);

  return {
    isConnected,
    lastUpdate,
    connectToRealTimeUpdates
  };
};